import React from 'react'
import { Box, Typography, Button, Alert, AlertTitle } from '@mui/material'
import { theme } from 'common/theme'

interface ErrorMessageProps {
  error: string
  onRetry: () => void
}

function ErrorMessage({ error, onRetry }: ErrorMessageProps) {
  return (
    <Box
      sx={{
        width: '100%',
        height: '600px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        p: 4,
        textAlign: 'center',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          maxWidth: '500px',
          width: '100%',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 80,
            height: 80,
            borderRadius: '50%',
            backgroundColor: `${theme.palette.error.main}15`,
            mb: 3,
            position: 'relative',
            '&::before': {
              content: '"!"',
              fontSize: 40,
              fontWeight: 'bold',
              color: theme.palette.error.main,
              position: 'absolute',
            },
          }}
        />

        <Typography
          variant='h5'
          sx={{
            fontWeight: 600,
            mb: 2,
            color: theme.palette.common.black,
          }}
        >
          Something went wrong
        </Typography>

        <Alert
          severity='error'
          sx={{
            width: '100%',
            mb: 3,
            borderRadius: 2,
            '& .MuiAlert-message': {
              width: '100%',
            },
          }}
        >
          <AlertTitle sx={{ fontWeight: 600, mb: 1 }}>
            Unable to enable Awakening
          </AlertTitle>
          <Typography variant='body2' sx={{ mb: 1 }}>
            {error}
          </Typography>
        </Alert>

        <Box
          sx={{
            backgroundColor: theme.palette.grey.A100,
            borderRadius: 2,
            p: 3,
            mb: 3,
            width: '100%',
          }}
        >
          <Typography variant='body2' sx={{ mb: 2, fontWeight: 500 }}>
            What you can do:
          </Typography>
          <Box sx={{ textAlign: 'left' }}>
            <Typography variant='body2' sx={{ mb: 1 }}>
              • Check your internet connection
            </Typography>
            <Typography variant='body2' sx={{ mb: 1 }}>
              • Try again in a few minutes
            </Typography>
            <Typography variant='body2' sx={{ mb: 1 }}>
              • Refresh the page and start over
            </Typography>
            <Typography variant='body2'>
              • Contact support if the issue continues
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
          <Button
            variant='outlined'
            onClick={onRetry}
            sx={{
              flex: 1,
              borderColor: theme.palette.grey[300],
              color: theme.palette.common.black,
              '&:hover': {
                borderColor: theme.palette.grey[700],
                backgroundColor: theme.palette.grey.A100,
              },
            }}
          >
            Try Again
          </Button>
          <Button
            variant='contained'
            color='secondary'
            component='a'
            href={`mailto:support@xtramath.org?subject=Awakening%20Enable%20Error&body=Error%20details:%20${encodeURIComponent(
              error
            )}`}
            sx={{ flex: 1 }}
          >
            Contact Support
          </Button>
        </Box>
      </Box>
    </Box>
  )
}

export default ErrorMessage
